import React from 'react';
import { BrowserRouter, Routes, Route, useLocation, matchPath } from 'react-router-dom';
import { Spinner, ErrorBoundary } from '@mbbe/ui';
import { usePageTracking } from './hooks/usePageTracking';
import { useAttributionCapture } from './hooks/useAttributionCapture';
import Layout from './components/Layout';
import { CookieConsentBanner } from './components/CookieConsentBanner';
import { QuoteModalProvider } from './features/quote/QuoteModalContext';
import QuoteRoute from './features/quote/QuoteRoute';

const Home = React.lazy(() => import('./pages/Home'));
const Portfolio = React.lazy(() => import('./pages/Portfolio'));
const ProjectDetail = React.lazy(() => import('./pages/ProjectDetail'));
const Services = React.lazy(() => import('./pages/Services'));
const Pricing = React.lazy(() => import('./pages/Pricing'));
const Contact = React.lazy(() => import('./pages/Contact'));
const KvkkDisclosure = React.lazy(() => import('./pages/KvkkDisclosure'));
const LegalPage = React.lazy(() => import('./pages/LegalPage'));
const PrivacyPolicy = React.lazy(() => import('./pages/PrivacyPolicy'));
const NotFound = React.lazy(() => import('./pages/NotFound'));
const DesignSystem = React.lazy(() => import('./pages/DesignSystem'));

function AppRoutes() {
  const location = useLocation();
  const match = matchPath('/portfolio/:id', location.pathname);
  const projectId = match?.params?.id ? parseInt(match.params.id, 10) : undefined;

  usePageTracking(projectId);
  useAttributionCapture();

  return (
    <React.Suspense
      fallback={
        <div className="flex items-center justify-center min-h-screen bg-dark-900">
          <Spinner size="lg" className="text-brand-500" />
        </div>
      }
    >
      <Routes>
        <Route path="/" element={<Layout />}>
          <Route index element={<Home />} />
          <Route path="portfolio" element={<Portfolio />} />
          <Route path="portfolio/:id" element={<ProjectDetail />} />
          <Route path="services" element={<Services />} />
          <Route path="pricing" element={<Pricing />} />
          <Route path="contact" element={<Contact />} />
          <Route path="teklif-al" element={<QuoteRoute />} />
          <Route path="kvkk-aydinlatma-metni" element={<KvkkDisclosure />} />
          <Route path="gizlilik-politikasi" element={<PrivacyPolicy />} />
          <Route path="hizmet-sozlesmesi" element={<LegalPage slug="hizmet-sozlesmesi" />} />
          <Route path="iptal-iade-politikasi" element={<LegalPage slug="iptal-iade-politikasi" />} />
          <Route path="kullanim-kosullari" element={<LegalPage slug="kullanim-kosullari" />} />
          <Route path="design-system" element={<DesignSystem />} />
          <Route path="*" element={<NotFound />} />
        </Route>
      </Routes>
    </React.Suspense>
  );
}

function App() {
  // No timer-driven startup overlay: routes render as soon as their lazy chunk
  // resolves and the Suspense fallback in AppRoutes covers only real pending work.
  return (
    <BrowserRouter>
      <ErrorBoundary>
        <QuoteModalProvider>
          <AppRoutes />
          <CookieConsentBanner />
        </QuoteModalProvider>
      </ErrorBoundary>
    </BrowserRouter>
  );
}

export default App;
